import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "@/pages/Login";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiErr } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function AcceptInvite() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const { acceptInvite } = useAuth();
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("loading"); // loading | ok | invalid
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            if (!token) return setStatus("invalid");
            try {
                const { data } = await api.get(`/auth/invite/${token}`);
                setEmail(data.email); setStatus("ok");
            } catch { setStatus("invalid"); }
        })();
    }, [token]);

    const submit = async (e) => {
        e.preventDefault(); setErr("");
        if (password.length < 8) return setErr("Password must be at least 8 characters");
        if (password !== confirm) return setErr("Passwords do not match");
        setLoading(true);
        try {
            await acceptInvite({ token, name, password });
            navigate("/");
        } catch (e) { setErr(apiErr(e)); } finally { setLoading(false); }
    };

    if (status === "loading") return <AuthShell title="Activating invite…"><Loader2 className="w-5 h-5 animate-spin text-primary" /></AuthShell>;
    if (status === "invalid") return <AuthShell title="Invalid invite" subtitle="This invitation link is invalid or has expired. Please contact your administrator." />;

    return (
        <AuthShell title="Accept invitation" subtitle={`Create your account for ${email}`}>
            <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                    <Label>Full name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Jane Doe" data-testid="invite-name-input" />
                </div>
                <div className="space-y-1.5">
                    <Label>Password</Label>
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" data-testid="invite-password-input" />
                </div>
                <div className="space-y-1.5">
                    <Label>Confirm password</Label>
                    <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="••••••••" data-testid="invite-confirm-input" />
                </div>
                {err && <p className="text-sm text-loss" data-testid="invite-error">{err}</p>}
                <Button type="submit" className="w-full" disabled={loading} data-testid="invite-submit-btn">
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Create account
                </Button>
            </form>
        </AuthShell>
    );
}
